import axios from 'axios';
import moment from 'moment';

// Server endpoints (credentials stay server-side)
const API_BASE = process.env.VUE_APP_API_BASE || '';

// Per AliceBlue docs (https://ant.aliceblueonline.com/productdocumentation/Historical%20Data/),
// only "1" (1-minute) and "D" (daily) resolutions are supported. All other timeframes
// must be aggregated client-side from these base resolutions.
const TIMEFRAME_MAP = {
    '1m':  { resolution: '1', minutesPerBar: 1 },
    '2m':  { resolution: '1', minutesPerBar: 2 },
    '3m':  { resolution: '1', minutesPerBar: 3 },
    '5m':  { resolution: '1', minutesPerBar: 5 },
    '10m': { resolution: '1', minutesPerBar: 10 },
    '15m': { resolution: '1', minutesPerBar: 15 },
    '30m': { resolution: '1', minutesPerBar: 30 },
    '1H':  { resolution: '1', minutesPerBar: 60 },
    '2H':  { resolution: '1', minutesPerBar: 120 },
    '4H':  { resolution: '1', minutesPerBar: 240 },
    '1D':  { resolution: 'D', minutesPerBar: null },
    '1W':  { resolution: 'D', minutesPerBar: null, aggregate: 'week' },
    '1M':  { resolution: 'D', minutesPerBar: null, aggregate: 'month' }
};

// Supported timeframes for UI
export const ALICEBLUE_SUPPORTED_TFS = ['1m', '3m', '5m', '10m', '15m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];

export default {
    accessToken: null,
    userId: null,
    _pendingState: null,
    wsConnection: null,
    ws: null,
    symbols: [],
    reconnectAttempts: 0,
    _wsOnMessage: null,
    _wsOnError: null,
    _wsSymbols: null,

    // ─── Authentication (OAuth flow) ───

    /**
     * Get the AliceBlue OAuth login URL from the server.
     * The server builds the URL with redirect_uri pointing to /api/aliceblue/callback.
     */
    async getAuthUrl() {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/aliceblue/auth-url', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data && response.data.success) {
            this._pendingState = response.data.state;
            return response.data;
        }

        throw new Error(response.data?.error || 'Failed to generate auth URL');
    },

    /**
     * Complete the OAuth flow after the user finishes login in the popup.
     * Sends the state to the server, which looks up the auth code received
     * at /api/aliceblue/callback, exchanges it for a session token, and stores
     * the token in Clerk metadata for future sessions.
     */
    async completeLogin(state) {
        const stateToUse = state || this._pendingState;
        if (!stateToUse) {
            throw new Error('No pending AliceBlue auth state');
        }

        const token = await this._getClerkToken();
        const response = await axios.post(API_BASE + '/api/aliceblue/complete',
            { state: stateToUse },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (response.data && response.data.success) {
            this.accessToken = response.data.sessionToken;
            this.userId = response.data.userId;

            localStorage.setItem('aliceblue_access_token', this.accessToken);
            localStorage.setItem('aliceblue_userId', this.userId);

            this._pendingState = null;
            return { success: true, sessionToken: this.accessToken, userId: this.userId };
        }

        throw new Error(response.data.error || 'Failed to complete AliceBlue login');
    },

    /**
     * Restore session from Clerk metadata (server-side).
     */
    async restoreSessionFromServer() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/aliceblue/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.sessionToken) {
                this.accessToken = response.data.sessionToken;
                this.userId = response.data.userId;
                localStorage.setItem('aliceblue_access_token', this.accessToken);
                localStorage.setItem('aliceblue_userId', this.userId);
                return true;
            }
            return false;
        } catch (error) {
            console.warn('[AliceBlue] Could not restore session:', error.message);
            return false;
        }
    },

    /**
     * Disconnect AliceBlue — clear local + Clerk metadata.
     */
    async disconnectFromServer() {
        try {
            const token = await this._getClerkToken();
            if (token) {
                await axios.post(API_BASE + '/api/aliceblue/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.warn('[AliceBlue] Could not clear session on server:', error.message);
        }
        this.clearSession();
    },

    /**
     * Get the Clerk JWT token for authenticated API calls.
     */
    async _getClerkToken() {
        try {
            if (window.__vue_clerk && window.__vue_clerk.getToken) {
                return await window.__vue_clerk.getToken();
            }
            if (window.Clerk && window.Clerk.session) {
                return await window.Clerk.session.getToken();
            }
            if (window.Clerk && window.Clerk.getToken) {
                return await window.Clerk.getToken();
            }
        } catch (e) {
            console.warn('[AliceBlue] Could not get Clerk token:', e.message);
        }
        return null;
    },

    /**
     * Check if user has a valid AliceBlue session.
     */
    isAuthenticated() {
        return !!(this.accessToken || localStorage.getItem('aliceblue_access_token'));
    },

    /**
     * Load saved session from localStorage.
     */
    loadSavedSession() {
        this.accessToken = localStorage.getItem('aliceblue_access_token');
        this.userId = localStorage.getItem('aliceblue_userId');
        return this.accessToken;
    },

    /**
     * Validate the current AliceBlue session by calling the session endpoint.
     * Returns: { valid: true } if token works, { valid: false, expired: true } if expired.
     */
    async validateSession() {
        if (!this.isAuthenticated()) {
            return { valid: false, expired: false, neverConnected: true };
        }
        try {
            const token = await this._getClerkToken();
            if (!token) return { valid: false, expired: false };

            // Use the server-proxied session endpoint to test auth
            const response = await axios.get(API_BASE + '/api/aliceblue/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.sessionToken) {
                // Token exists on server, now verify it actually works with AliceBlue
                try {
                    // Try a simple API call to validate the session
                    const testResponse = await axios.get(API_BASE + '/api/aliceblue/search?query=RELIANCE&limit=1', {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (testResponse.data && testResponse.data.success) {
                        return { valid: true, expired: false };
                    } else {
                        return { valid: false, expired: true };
                    }
                } catch (testErr) {
                    const status = testErr.response && testErr.response.status;
                    if (status === 401 || status === 403) {
                        return { valid: false, expired: true };
                    }
                    // Network error or other issue — assume valid to avoid false positives
                    return { valid: true, expired: false };
                }
            }
            return { valid: false, expired: false, neverConnected: true };
        } catch (error) {
            console.warn('[AliceBlue] validateSession error:', error.message);
            return { valid: false, expired: false };
        }
    },

    /**
     * Clear session and remove stored credentials.
     */
    clearSession() {
        this.accessToken = null;
        this.userId = null;
        this._pendingState = null;
        localStorage.removeItem('aliceblue_access_token');
        localStorage.removeItem('aliceblue_userId');
        this.closeWebSocket();
    },

    // ─── Symbol Search ───

    /**
     * Search symbols via AliceBlue API through backend proxy.
     */
    async searchSymbols(query, limit = 30) {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/aliceblue/search', {
                params: {
                    query: query.toUpperCase(),
                    limit
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
            return [];
        } catch (error) {
            console.error('[AliceBlue] search error:', error);
            return [];
        }
    },

    /**
     * Load symbol master — delegates to search for backward compatibility.
     */
    async loadSymbolMaster(exchange = 'NSE') {
        return this.searchSymbols('A', 100);
    },

    /**
     * Load exchange symbols (backward compatibility).
     */
    async loadExchangeSymbols(exchange = 'NSE') {
        return this.searchSymbols('A', 100);
    },

    // ─── Historical Data ───

    /**
     * Calculate the from-date (in ms) based on timeframe.
     * Picks a window large enough to populate enough bars at the requested aggregation.
     */
    _calculateFromDate(timeframe, endTime) {
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();
        const cfg = TIMEFRAME_MAP[timeframe];

        // Days lookback for each effective timeframe. For minute-based aggregations the
        // server returns 1m candles, so windows must be small enough to avoid huge payloads.
        const OPTIMAL_DAYS = {
            '1m': 3,
            '2m': 4,
            '3m': 5,
            '5m': 7,
            '10m': 10,
            '15m': 15,
            '30m': 25,
            '1H': 40,
            '2H': 60,
            '4H': 90,
            '1D': 365,
            '1W': 365 * 3,
            '1M': 365 * 5
        };

        const safeDays = OPTIMAL_DAYS[timeframe] || 30;
        return now.clone().subtract(safeDays, 'days').valueOf(); // milliseconds
    },

    /**
     * Load historical candle data through backend proxy.
     * Returns array of [timestamp, open, high, low, close, volume].
     */
    async loadHistoricalData(symbol, timeframe, _retryCount, endTime) {
        const retryCount = _retryCount || 0;

        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with AliceBlue');
        }

        if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
            console.error('[AliceBlue] loadHistoricalData called with invalid symbol:', symbol);
            throw new Error('Symbol is required but not provided');
        }

        const cfg = TIMEFRAME_MAP[timeframe];
        if (!cfg) {
            console.error('[AliceBlue] Invalid timeframe:', timeframe);
            throw new Error(`Invalid timeframe: ${timeframe}. Supported: ${Object.keys(TIMEFRAME_MAP).join(', ')}`);
        }

        const resolution = cfg.resolution; // '1' or 'D'
        // AliceBlue history API expects from/to in MILLISECONDS (per docs)
        const to = Number.isFinite(endTime) ? endTime : moment().valueOf();
        const from = this._calculateFromDate(timeframe, endTime);

        // Parse symbol — expected format: exchange:token (e.g., NSE:26000)
        // AliceBlue history API requires the numeric instrument token, not the symbol name
        let exchange = 'NSE';
        let symbolCode = symbol.trim();
        if (symbol.includes(':')) {
            const parts = symbol.split(':');
            exchange = parts[0];
            symbolCode = parts[1];
        }


        try {
            const token = await this._getClerkToken();
            if (!token) throw new Error('Not authenticated');

            const response = await axios.get(API_BASE + '/api/aliceblue/history', {
                params: {
                    token: symbolCode,
                    exchange,
                    resolution,
                    from,
                    to
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success && response.data.candles) {
                let candles = response.data.candles;

                // Filter out invalid candles
                candles = candles.filter(candle => {
                    return candle && candle.length === 6 &&
                        !isNaN(candle[1]) && !isNaN(candle[2]) &&
                        !isNaN(candle[3]) && !isNaN(candle[4]) &&
                        candle[1] > 0 && candle[2] > 0 && candle[3] > 0 && candle[4] > 0;
                });


                // Aggregate based on timeframe configuration
                if (cfg.minutesPerBar && cfg.minutesPerBar > 1) {
                    candles = this._aggregateMinutes(candles, cfg.minutesPerBar);
                } else if (cfg.aggregate === 'week' || cfg.aggregate === 'month') {
                    candles = this._aggregateCandles(candles, timeframe);
                }

                return candles;
            } else {
                const errMsg = response.data?.error || 'Failed to load historical data';
                throw new Error(errMsg);
            }
        } catch (error) {
            const status = error.response && error.response.status;
            const authExpired = error.response?.data?.authExpired;

            if (status === 401 && authExpired) {
                console.error('[AliceBlue] Session expired, clearing session');
                this.clearSession();
                throw new Error('AliceBlue session expired. Please reconnect your AliceBlue account.');
            }

            // Retry on rate-limit (429) or server errors (5xx / 502 from Cloudflare).
            // Honor server-supplied Retry-After (in seconds) when present; otherwise
            // use exponential backoff (2s, 4s, 8s) plus a small jitter to avoid
            // thundering herds when many timeframes are loading simultaneously.
            const retryable = status === 429 || status === 502 || status === 503 || status === 504
                || (status >= 500 && status < 600);
            if (retryCount < 3 && retryable) {
                const retryAfterHeader = error.response?.headers?.['retry-after'];
                const retryAfterBody = error.response?.data?.retryAfter;
                const serverHint = parseInt(retryAfterHeader || retryAfterBody, 10);
                const baseBackoff = Number.isFinite(serverHint) && serverHint > 0
                    ? serverHint * 1000
                    : Math.min(2000 * Math.pow(2, retryCount), 15000);
                const jitter = Math.floor(Math.random() * 400);
                const backoff = baseBackoff + jitter;
                console.warn(`[AliceBlue] Rate limited (${status}), retrying in ${backoff}ms (attempt ${retryCount + 1}/3)...`);
                await new Promise(r => setTimeout(r, backoff));
                return this.loadHistoricalData(symbol, timeframe, retryCount + 1, endTime);
            }

            console.error('[AliceBlue] loadHistoricalData error:', error.message);
            throw error;
        }
    },

    /**
     * Aggregate 1-minute candles into N-minute candles (e.g., 5m, 15m, 1H, 4H).
     * Uses bucket boundary aligned to the bar size from epoch.
     */
    _aggregateMinutes(candles, minutesPerBar) {
        if (!candles || candles.length === 0 || !minutesPerBar || minutesPerBar <= 1) {
            return candles || [];
        }
        const bucketMs = minutesPerBar * 60 * 1000;
        const aggregated = [];
        let bucketStart = null;
        let group = [];

        for (const c of candles) {
            const ts = c[0];
            const bs = Math.floor(ts / bucketMs) * bucketMs;
            if (bucketStart === null || bs !== bucketStart) {
                if (group.length) aggregated.push(this._combineCandles(group, bucketStart));
                bucketStart = bs;
                group = [c];
            } else {
                group.push(c);
            }
        }
        if (group.length) aggregated.push(this._combineCandles(group, bucketStart));

        return aggregated;
    },

    /**
     * Aggregate daily candles into weekly or monthly candles.
     */
    _aggregateCandles(candles, timeframe) {
        if (!candles || candles.length === 0) return [];

        const aggregated = [];
        let currentGroup = [];
        let currentPeriod = null;

        for (const candle of candles) {
            const timestamp = candle[0];
            const date = moment(timestamp);
            let periodKey;

            if (timeframe === '1W') {
                periodKey = date.format('YYYY-WW');
            } else if (timeframe === '1M') {
                periodKey = date.format('YYYY-MM');
            }

            if (periodKey !== currentPeriod) {
                if (currentGroup.length > 0) {
                    aggregated.push(this._combineCandles(currentGroup));
                }
                currentGroup = [candle];
                currentPeriod = periodKey;
            } else {
                currentGroup.push(candle);
            }
        }

        if (currentGroup.length > 0) {
            aggregated.push(this._combineCandles(currentGroup));
        }

        return aggregated;
    },

    /**
     * Combine a group of candles into a single candle.
     * Optionally pass `bucketStart` to use that as the bar timestamp (aligned bucket boundary).
     */
    _combineCandles(group, bucketStart) {
        if (!group || group.length === 0) return null;
        if (group.length === 1) {
            return bucketStart != null
                ? [bucketStart, group[0][1], group[0][2], group[0][3], group[0][4], group[0][5]]
                : group[0];
        }

        const firstCandle = group[0];
        const lastCandle = group[group.length - 1];

        let high = firstCandle[2];
        let low = firstCandle[3];
        let volume = 0;

        for (const candle of group) {
            high = Math.max(high, candle[2]);
            low = Math.min(low, candle[3]);
            volume += candle[5];
        }

        return [
            bucketStart != null ? bucketStart : firstCandle[0],
            firstCandle[1],  // Open of first candle
            high,
            low,
            lastCandle[4],   // Close of last candle
            volume
        ];
    },

    // ─── WebSocket (Real-time Data) ───

    /**
     * Connect to AliceBlue WebSocket for real-time market data.
     *
     * Per AliceBlue docs (https://ant.aliceblueonline.com/productdocumentation/Websocket/):
     * 1. Open WS to wss://ws1.aliceblueonline.com/NorenWS
     * 2. Send connection payload: { susertoken, t:"c", actid, uid, source }
     * 3. Wait for {"t":"cf","k":"OK"} acknowledgement
     * 4. Send subscription: { k: "EXCH|TOKEN#EXCH|TOKEN", t: "t" }
     * 5. Send heartbeat { k:"", t:"h" } every ~50 seconds
     */
    async connectWebSocket(symbols, onMessage) {
        if (!this.isAuthenticated()) {
            console.error('[AliceBlue] Cannot connect WebSocket: not authenticated');
            return null;
        }

        this.closeWebSocket();

        try {
            const token = await this._getClerkToken();
            if (!token) {
                console.error('[AliceBlue] Missing Clerk token for WebSocket');
                return null;
            }

            // Get WebSocket URL and connection payload from server
            const response = await axios.get(API_BASE + '/api/aliceblue/ws-url', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data || !response.data.success || !response.data.wsUrl) {
                console.error('[AliceBlue] Failed to get WebSocket URL');
                return null;
            }

            const wsUrl = response.data.wsUrl;
            const connectionPayload = response.data.connectionPayload;
            if (!connectionPayload || !connectionPayload.susertoken) {
                console.error('[AliceBlue] Missing connection payload from server');
                return null;
            }


            this._wsSymbols = symbols;
            this._wsOnMessage = onMessage;
            this._wsAuthenticated = false;

            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                // Send connect message, do NOT subscribe yet — wait for cf/OK ack
                try {
                    this.ws.send(JSON.stringify(connectionPayload));
                } catch (e) {
                    console.error('[AliceBlue] Failed to send connect payload:', e);
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    let data;
                    if (typeof event.data === 'string') {
                        data = JSON.parse(event.data);
                    } else if (event.data instanceof ArrayBuffer) {
                        try {
                            const text = new TextDecoder().decode(event.data);
                            data = JSON.parse(text);
                        } catch (e) {
                            data = this._parseBinaryMessage(event.data);
                        }
                    }

                    if (!data) return;

                    // Connection acknowledgement: {"t":"cf","k":"OK"} (or "failed")
                    if (data.t === 'cf') {
                        if (data.k === 'OK') {
                            this._wsAuthenticated = true;
                            this.reconnectAttempts = 0;

                            // Subscribe to symbols now that auth succeeded
                            if (this._wsSymbols && Array.isArray(this._wsSymbols) && this._wsSymbols.length) {
                                this._subscribeSymbols(this._wsSymbols);
                            }

                            // Start heartbeat (every 50s per docs)
                            if (this._heartbeatInterval) clearInterval(this._heartbeatInterval);
                            this._heartbeatInterval = setInterval(() => {
                                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                                    try { this.ws.send(JSON.stringify({ k: '', t: 'h' })); } catch (_) {}
                                }
                            }, 50000);
                        } else {
                            console.error('[AliceBlue] WebSocket auth failed:', data);
                            if (this._wsOnError) this._wsOnError(new Error('AliceBlue WebSocket authentication failed'));
                            try { this.ws.close(1000, 'Auth failed'); } catch (_) {}
                        }
                        return;
                    }

                    // Forward tick/depth feed to consumer
                    if (onMessage) {
                        const normalized = this._normalizeWsMessage(data);
                        if (normalized) onMessage(normalized);
                    }
                } catch (error) {
                    console.error('[AliceBlue] Error processing WS message:', error);
                }
            };

            this.ws.onerror = (error) => {
                console.error('[AliceBlue] WebSocket error:', error);
                if (this._wsOnError) this._wsOnError(error);
            };

            this.ws.onclose = (event) => {

                if (event.code === 1000 || !this.isAuthenticated()) {
                    return;
                }

                // Reconnect with exponential backoff
                if (this.reconnectAttempts < 5) {
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    this.reconnectAttempts++;
                    setTimeout(() => {
                        this.connectWebSocket(this._wsSymbols, this._wsOnMessage);
                    }, delay);
                }
            };

            return this.ws;
        } catch (error) {
            console.error('[AliceBlue] WebSocket connection error:', error);
            return null;
        }
    },

    /**
     * Subscribe to symbols on the WebSocket.
     * AliceBlue format per docs: {"k":"EXCHANGE|TOKEN#EXCHANGE|TOKEN","t":"t"}
     */
    _subscribeSymbols(symbols) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[AliceBlue] WebSocket not connected, cannot subscribe');
            return;
        }

        const tokenList = symbols.map(s => {
            if (s.includes(':')) {
                const parts = s.split(':');
                return parts[0] + '|' + parts[1];
            }
            return 'NSE|' + s;
        });

        // AliceBlue subscription: {"k":"NSE|26000#NSE|26009","t":"t"}
        const subscribeMsg = {
            k: tokenList.join('#'),
            t: 't'
        };

        try {
            this.ws.send(JSON.stringify(subscribeMsg));
        } catch (error) {
            console.error('[AliceBlue] Failed to send subscription:', error);
        }
    },

    /**
     * Subscribe to a single symbol.
     * AliceBlue format: {"k":"NSE|26000","t":"t"}
     */
    subscribeToSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this._wsAuthenticated) {
            console.warn('[AliceBlue] WebSocket not ready (auth pending), cannot subscribe to:', symbol);
            return;
        }

        let tokenStr;
        if (symbol.includes(':')) {
            const parts = symbol.split(':');
            tokenStr = parts[0] + '|' + parts[1];
        } else {
            tokenStr = 'NSE|' + symbol;
        }

        const subscribeMsg = { k: tokenStr, t: 't' };

        try {
            this.ws.send(JSON.stringify(subscribeMsg));
        } catch (error) {
            console.error('[AliceBlue] Failed to subscribe:', error);
        }
    },

    /**
     * Unsubscribe from a symbol.
     * AliceBlue format: {"k":"NSE|26000","t":"u"}
     */
    unsubscribeFromSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[AliceBlue] WebSocket not connected, cannot unsubscribe from:', symbol);
            return;
        }

        let tokenStr;
        if (symbol.includes(':')) {
            const parts = symbol.split(':');
            tokenStr = parts[0] + '|' + parts[1];
        } else {
            tokenStr = 'NSE|' + symbol;
        }

        const unsubscribeMsg = { k: tokenStr, t: 'u' };

        try {
            this.ws.send(JSON.stringify(unsubscribeMsg));
        } catch (error) {
            console.error('[AliceBlue] Failed to unsubscribe:', error);
        }
    },

    /**
     * Close WebSocket connection.
     */
    closeWebSocket() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close(1000, 'Manual close');
            this.ws = null;
        }
        if (this.wsConnection) {
            this.wsConnection = null;
        }
        this.reconnectAttempts = 0;
        this._wsSymbols = null;
        this._wsAuthenticated = false;
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    },

    destroyWs() {
        this.closeWebSocket();
    },

    /**
     * Alias for connectWebSocket for backward compatibility.
     */
    createWebSocket(onMessage, onError, symbols) {
        this._wsOnError = onError;
        return this.connectWebSocket(symbols || null, onMessage);
    },

    // ─── Utility ───

    /**
     * Parse binary WebSocket message.
     */
    _parseBinaryMessage(buffer) {
        try {
            const text = new TextDecoder().decode(buffer);
            if (text.startsWith('{') || text.startsWith('[')) {
                return JSON.parse(text);
            }
        } catch (e) {
            // Not text/JSON
        }
        console.warn('[AliceBlue] Received binary message that could not be parsed');
        return null;
    },

    /**
     * Normalize WebSocket message to common format used by the chart.
     */
    _normalizeWsMessage(data) {
        if (!data) return null;

        // AliceBlue WebSocket responses per docs:
        // t:"tk" = tick acknowledgement, t:"tf" = tick feed
        // Fields: e=exchange, tk=token, lp=LTP, pc=percent change,
        // v=volume, o=open, h=high, l=low, c=close, ft=feed time
        if (data.t === "tf" || data.t === "tk" || data.t === "df" || data.t === "dk") {
            const ltp = parseFloat(data.lp) || 0;
            if (ltp <= 0) return null;
            return {
                symbol: (data.e || "NSE") + ":" + (data.tk || ""),
                ltp: ltp,
                timestamp: parseInt(data.ft) * 1000 || Date.now(),
                volume: parseInt(data.v) || 0,
                open_price: parseFloat(data.o) || 0,
                high_price: parseFloat(data.h) || 0,
                low_price: parseFloat(data.lo || data.l) || 0,
                close_price: parseFloat(data.c) || 0,
                type: "aliceblue_tick"
            };
        }

        return data;
    },

    /**
     * Normalize a symbol to AliceBlue format.
     * AliceBlue uses: exchange:token (e.g., NSE:26000)
     */
    _normalizeAliceBlueSymbol(symbol) {
        if (!symbol) return '';

        // Already in AliceBlue format
        if (symbol.includes(':')) {
            return symbol;
        }

        // Plain symbol — assume NSE
        return 'NSE:' + symbol.toUpperCase();
    },

    /**
     * Convert AliceBlue symbol to display format.
     */
    getDisplaySymbol(symbol) {
        if (!symbol) return '';
        const parts = symbol.split(':');
        return parts.length > 1 ? parts[1] : parts[0];
    },

    /**
     * Check if AliceBlue authentication is completed (for Tauri polling)
     * Returns authentication status from server
     */
    async checkAuthStatus() {
        try {
            const token = await this._getClerkToken();
            if (!token) return { success: false };

            const response = await axios.get(API_BASE + '/api/aliceblue/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.sessionToken) {
                this.accessToken = response.data.sessionToken;
                this.userId = response.data.userId;

                localStorage.setItem('aliceblue_access_token', this.accessToken);
                localStorage.setItem('aliceblue_userId', this.userId);

                return { success: true, sessionToken: this.accessToken, userId: this.userId };
            }

            return { success: false };
        } catch (error) {
            return { success: false };
        }
    }
};
